import { Injectable } from '@angular/core';
import { HttpRestService } from '@core/services/http-rest.service';
import { BBGM_APPLICANT_SUBDOMAIN, PortalDeterminationService } from '@core/services/portal-determination.service';
import { BlackbaudSsoRouteResponse, ClientForUser, DetermineSignInFlow, LinkToBbidPayload } from '@core/typings/auth.typing';
import { UserTypes } from '@core/typings/client-user.typing';

@Injectable({ providedIn: 'root' })
export class AuthResources {

  constructor (
    private httpRestService: HttpRestService,
    private portal: PortalDeterminationService
  ) { }

  acceptTermsOfService () {
    const endpoint = this.portal.isManager ?
      'api/manager/user/AcceptTermsOfService' :
      'api/portal/applicant/AcceptTermsOfService';

    return this.httpRestService.post(endpoint, {});
  }

  getBlackbaudLoginRoute (): Promise<BlackbaudSsoRouteResponse> {
    const endpoint = 'api/admin/Sso/SignInUrl';

    return this.httpRestService.get(endpoint);
  }

  determineSignInFlow (
    emailAddress: string,
    clientSubdomain: string
  ): Promise<DetermineSignInFlow> {
    const userType = this.portal.getUserType();
    const payload = {
      emailAddress,
      userType,
      // Subdomain used for BBGM applicants not relevant here
      clientSubdomain: clientSubdomain === BBGM_APPLICANT_SUBDOMAIN ? undefined : clientSubdomain
    };
    const endpoint = 'api/token/DetermineUserSigninFlow';

    return this.httpRestService.post(endpoint, payload);
  }

  getClientsForUser (
    email: string,
    blackbaudId: string
  ): Promise<ClientForUser[]> {
    let endpoint = 'api/manager/user/ClientUserSubdomains?';
    if (!!email) {
      endpoint = endpoint + `email=${encodeURIComponent(email)}`;
    }
    if (!!blackbaudId) {
      endpoint = endpoint + `${!!email ? '&' : ''}blackbaudId=${encodeURIComponent(blackbaudId)}`
    }

    return this.httpRestService.get(endpoint);
  }

  linkToBbidAccount (payload: LinkToBbidPayload) {
    const endpoint = 'api/token/LinkToBbidAccount';

    return this.httpRestService.post(endpoint, payload);
  }

  async createApplicantAccountForBbid (
    firstName: string,
    lastName: string,
    email: string,
    languageCulture: string,
    grantProgramGuid: string
  ) {
    const endpoint = 'api/portal/applicant/CreateForBbid';

    return this.httpRestService.post(endpoint, {
      email,
      firstName,
      lastName,
      languageCulture,
      grantProgramGuid
    });
  }

  verifyBbidLink () {
    const endpoint = this.portal.isManager ?
      'api/manager/user/VerifyBbidLink' :
      'api/portal/applicant/VerifyBbidLink';

    return this.httpRestService.get(endpoint);
  }

  trackBbidLogin (clientId: number) {
    const endpoint = 'api/token/BbidLogin';

    return this.httpRestService.post(endpoint, {
      clientId
    });
  }

  sendVerificationCode (
    email: string,
    userType: UserTypes,
    clientId: number
  ) {
    const endpoint = 'api/token/ResendMultifactorAuthenticationToken';

    return this.httpRestService.post(endpoint, {
      email,
      userType,
      clientId
    });
  }
}
